import responseHelper from '../../services/ResponseHelper';
import calendarEventService from '../../services/calendarEventService';
import CoreBase from '../../services/engines/CoreBase';
import { CalEventStates } from '../Types/CalEventStates';
import calendarCache from './CalendarCache';

import config from '../../config.json';

/**
 * CalendarUpdater — persists CalEvent mutations to the backend.
 * Mutate + POST/PATCH + check response, that's the whole job — no notify,
 * no orchestration. CalendarEngine awaits these calls and owns the single
 * 'dataChanged' notify afterwards (one notify point, no doNotify flag
 * threading through multiple layers).
 * Singleton — same shape as CalendarCache/CalendarScheduler.
 */
class CalendarUpdater extends CoreBase {
    // Class field — overrides CoreBase._name so logError shows correct source
    _name = 'CalendarUpdater';

    /**
     * Persists a partial CalEvent update — position and/or stateId.
     * PATCH /calEvent/event via calendarEventService.patchCalendarEvent.
     * Replaces the old separate updatePosition (PATCH /calEvent/position)
     * and updateState (POST /calEvent/event) — two endpoints for what is
     * the same kind of partial mutation. BE now accepts both fields on the
     * one PATCH /calEvent/event route.
     * event.setPos() does posX/posY + pos in 1 step, same derivation the
     * CalEventObj constructor uses — no separate, possibly drifting writes.
     * @param {CalEventObj} event
     * @param {{ pos?: { top: number, left: number }, stateId?: number }} changes
     * @returns {CalEventObj|null} event on success, null on failure
     */
    patchCalEvent = async (event, { pos, stateId } = {}) => {
        try {
            if (!event) return null;

            if (pos) event.setPos(pos);
            if (stateId !== undefined) event.stateId = stateId;

            calendarCache.addOrUpdate([event]);

            const resp = await calendarEventService.patchCalendarEvent(event);
            return this._checkResp(resp, 'patchCalEvent', event);
        } catch (error) {
            this.logError('patchCalEvent', error.message);
            return null;
        }
    };

    /**
     * Persists field edits from EditEventDialog, or creates a brand new
     * event when event.rk is empty. POST /calEvent/event.
     * TODO: PATCH full event via calendarEventService.patchCalendarEvent()
     * for existing events instead of POST.
     * @param {CalEventObj} event
     * @param {object} changes - { label?, date?, time?, cat?, desc?, remind? }
     * @returns {*} response payload on success, null on failure
     */
    updateEvent = async (event, changes) => {
        try {
            if (!changes.app) {
                changes.app = config.appName;
            }

            // when rk is empty this is a new event — post the draft fields directly
            if (!event?.rk) {
                const resp = await calendarEventService.postCalendarEvent(changes);
                return this._checkResp(resp, 'updateEvent', changes);
            }

            // else update current
            Object.assign(event, changes);

            // date/time changed — recompute ems via setDateTime() (single derivation
            // point, same as setPos()/_derivePos()). Without this, ems stays stale and
            // isOverDue()/isRinging()/CalendarScheduler transitions keep using the old
            // timestamp even though the date/time display fields already changed.
            if (changes.date !== undefined || changes.time !== undefined) {
                event.setDateTime(event.date, event.time);
            }

            // check if ems in future, set stateId accordingly
            // this is for updating overdue event to a new future date
            if (event.isOverDue() && event.stateId === CalEventStates.Completed) {
                event.stateId = CalEventStates.Active;
            }

            calendarCache.addOrUpdate([event]);

            const resp = await calendarEventService.postCalendarEvent(event);
            return this._checkResp(resp, 'updateEvent', event);
        } catch (error) {
            this.logError('updateEvent', error.message);
            return null;
        }
    };

    // ─── private ──────────────────────────────────────────

    /**
     * Checks a backend response. responseHelper.check() returns a positive
     * code (200/201/202/410) on success, negative on failure — code > 0 is
     * the correct success check. CalendarEngine's old _postCalendarEvent
     * used `code < 0` for failure instead, which treats a 0/-0 code as
     * success — that gap is closed here.
     * @param {*} resp
     * @param {string} method
     * @param {*} value - returned as-is on success
     * @returns {*|null}
     */
    _checkResp = (resp, method, value) => {
        const code = responseHelper.check(resp, '', this._name);
        if (code > 0) {
            return value;
        }
        this.logError(method, `failed — code=${code}`);
        return null;
    };
}

const calendarUpdator = new CalendarUpdater();
export default calendarUpdator;
